/**
 * EmailAttachmentBar
 *
 * Reusable file-attachment UI for the email composer (used in InboxModeEmail
 * and NormalModeEmail). Uploads files to Firebase Storage at
 *   email_attachments/<userId>/<timestamp>_<random>_<safeName>
 * and exposes only metadata via the controlled `attachments` prop. The actual
 * file bytes never travel through the React app's state once uploaded — the
 * cloud function pulls them from Storage with the admin SDK right before the
 * SMTP/Graph send so the recipient receives a real RFC MIME attachment.
 *
 * Why metadata (storagePath) and not data URLs?
 *   - The Cloud Tasks payload limit is 100 KB. Attaching base64 buffers in the
 *     scheduled-send pipeline would break it for anything > ~70 KB.
 *   - Real RFC attachments need backend-side bytes anyway (nodemailer's
 *     `attachments` field accepts Buffers / Streams, not URLs from another
 *     origin reliably).
 *   - Keeping bytes server-side keeps them private; the Storage URL is never
 *     embedded in the email body.
 */
import React, { useRef, useState } from 'react';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';
import './EmailAttachmentBar.css';

const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const MAX_FILE_COUNT = 10;

const ALLOWED_EXTENSIONS = new Set([
  'pdf',
  'doc', 'docx',
  'xls', 'xlsx',
  'ppt', 'pptx',
  'txt', 'csv', 'rtf',
  'zip',
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic',
]);

const ALLOWED_ACCEPT = [
  'application/pdf',
  '.doc', '.docx',
  '.xls', '.xlsx',
  '.ppt', '.pptx',
  '.txt', '.csv', '.rtf',
  '.zip',
  'image/*',
].join(',');

const sanitizeFileName = (name) => {
  if (!name || typeof name !== 'string') return 'file';
  const trimmed = name.trim().slice(0, 180);
  return trimmed.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_');
};

const getExtension = (name) => {
  if (!name || typeof name !== 'string') return '';
  const idx = name.lastIndexOf('.');
  if (idx === -1 || idx === name.length - 1) return '';
  return name.slice(idx + 1).toLowerCase();
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const EmailAttachmentBar = ({
  userId,
  attachments,
  onChange,
  disabled = false,
  fileInputRef,
}) => {
  const internalInputRef = useRef(null);
  const inputRef = fileInputRef || internalInputRef;
  const [error, setError] = useState('');

  const totalBytes = (attachments || []).reduce((acc, a) => acc + (a.size || 0), 0);

  const handleFiles = async (fileList) => {
    setError('');
    if (!userId) {
      setError('Not signed in.');
      return;
    }
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const current = Array.isArray(attachments) ? attachments : [];
    if (current.length + files.length > MAX_FILE_COUNT) {
      setError(`Max ${MAX_FILE_COUNT} files per email.`);
      return;
    }

    // Validate every file before kicking off any upload — fail fast with a
    // single error message instead of half-uploading a batch.
    let runningTotal = totalBytes;
    for (const file of files) {
      const ext = getExtension(file.name);
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        setError(`File type "${ext || 'unknown'}" is not allowed.`);
        return;
      }
      runningTotal += file.size;
      if (runningTotal > MAX_TOTAL_BYTES) {
        setError(`Attachments exceed 25 MB total.`);
        return;
      }
    }

    // Optimistically push placeholder rows so the user gets immediate feedback.
    const placeholders = files.map((file) => ({
      tempId: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      filename: file.name,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      uploading: true,
      progress: 0,
      storagePath: null,
      downloadURL: null,
      error: null,
    }));

    let next = [...current, ...placeholders];
    onChange(next);

    await Promise.all(
      files.map((file, idx) => {
        const placeholder = placeholders[idx];
        const safeName = sanitizeFileName(file.name);
        const path = `email_attachments/${userId}/${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 8)}_${safeName}`;
        const fileRef = storageRef(storage, path);
        const task = uploadBytesResumable(fileRef, file, {
          contentType: file.type || 'application/octet-stream',
          customMetadata: { originalFilename: file.name },
        });

        return new Promise((resolve) => {
          task.on(
            'state_changed',
            (snap) => {
              const pct = snap.totalBytes > 0
                ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                : 0;
              next = next.map((a) =>
                a.tempId === placeholder.tempId ? { ...a, progress: pct } : a
              );
              onChange(next);
            },
            (err) => {
              console.error('[EMAIL-ATTACH] Upload failed:', err);
              next = next.map((a) =>
                a.tempId === placeholder.tempId
                  ? { ...a, uploading: false, error: err.message || 'Upload failed' }
                  : a
              );
              onChange(next);
              resolve();
            },
            async () => {
              // The download URL is a convenience only — the send path resolves
              // bytes from `storagePath` with the admin SDK. Team members write
              // into the world owner's folder and cannot read it back, so a
              // failing getDownloadURL must not invalidate the attachment.
              let url = null;
              try {
                url = await getDownloadURL(task.snapshot.ref);
              } catch (urlErr) {
                console.warn('[EMAIL-ATTACH] Download URL unavailable, continuing with storagePath:', urlErr?.message);
              }
              next = next.map((a) =>
                a.tempId === placeholder.tempId
                  ? {
                      ...a,
                      uploading: false,
                      progress: 100,
                      storagePath: path,
                      downloadURL: url,
                    }
                  : a
              );
              onChange(next);
              resolve();
            }
          );
        });
      })
    );
  };

  const handleRemove = (tempId) => {
    const current = Array.isArray(attachments) ? attachments : [];
    onChange(current.filter((a) => a.tempId !== tempId));
  };

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    // Reset so picking the same file twice in a row still triggers `onChange`.
    if (e.target) e.target.value = '';
  };

  const list = Array.isArray(attachments) ? attachments : [];

  if (list.length === 0 && !error) {
    return (
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ALLOWED_ACCEPT}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="emailAttachmentBar">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ALLOWED_ACCEPT}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
      {error && <div className="emailAttachmentBarError">{error}</div>}
      {list.length > 0 && (
        <div className="emailAttachmentBarList">
          {list.map((a) => (
            <div
              key={a.tempId}
              className={`emailAttachmentChip ${a.error ? 'has-error' : ''} ${a.uploading ? 'is-uploading' : ''}`}
              title={a.error || a.filename}
            >
              <span className="emailAttachmentChipIcon" aria-hidden="true">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                </svg>
              </span>
              <span className="emailAttachmentChipName">{a.filename}</span>
              {!a.error && (
                <span className="emailAttachmentChipSize">{formatSize(a.size)}</span>
              )}
              {a.uploading && (
                <span className="emailAttachmentChipProgress">{a.progress || 0}%</span>
              )}
              {a.error && (
                <span className="emailAttachmentChipErrorLabel">{a.error}</span>
              )}
              <button
                type="button"
                className="emailAttachmentChipRemove"
                onClick={() => handleRemove(a.tempId)}
                disabled={disabled}
                aria-label={`Remove ${a.filename}`}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6L18 18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Returns the wire-format payload to send to the cloud function. Drops files
 * that failed to upload or are still uploading.
 */
EmailAttachmentBar.toRequestPayload = (attachments) => {
  if (!Array.isArray(attachments)) return [];
  return attachments
    .filter((a) => a && a.storagePath && !a.uploading && !a.error)
    .map((a) => ({
      filename: a.filename,
      storagePath: a.storagePath,
      contentType: a.contentType,
      size: a.size,
    }));
};

EmailAttachmentBar.hasPending = (attachments) =>
  Array.isArray(attachments) && attachments.some((a) => a && a.uploading);

EmailAttachmentBar.hasErrors = (attachments) =>
  Array.isArray(attachments) && attachments.some((a) => a && a.error);

export default EmailAttachmentBar;
